/**
 * A **development-only** account switcher for the testing period: pick one of the
 * local issuer's dev accounts and be signed in as them.
 *
 * ## Why this is not the role selector the UI spec rules out
 *
 * UI spec §0.1 excludes the prototype role selector of §2.2, and that exclusion
 * stands: *"there is no user-facing role switcher"*, and `TopBar` says the same.
 * This control does something different in kind. It does not tell the client to
 * pretend to hold other roles, and it cannot - roles arrive from `GET /api/me`,
 * resolved server-side per request from `user_role_assignment` and
 * `role_group_mapping`. What it does is start a **real sign-in as a different
 * account**: it navigates to `/auth/login`, the full authorization-code + PKCE
 * exchange runs, and whatever roles that account actually has come back the
 * ordinary way. Nothing about the authorization model is bypassed or weakened; the
 * switcher just saves retyping a username at the issuer.
 *
 * ## Why it cannot appear in a real deployment
 *
 * Three independent reasons, in order of how hard they are to defeat:
 *
 * 1. **It is compiled out.** The guard is `import.meta.env.DEV`, which Vite
 *    replaces with the literal `false` in `vite build`; the early return then
 *    makes the rest of the component unreachable and the account list unreferenced,
 *    so both are dropped. Verified by grepping the built bundle - the component
 *    name, every subject and every label are absent from `dist/assets/*.js`. There
 *    is no runtime flag to misconfigure.
 *
 *    Two honest caveats. The emitted **source map** does embed this file's text,
 *    as it does for all source (`vite.config.ts` sets `sourcemap: true`); a map is
 *    inert source, not executed code, but it is a reason not to publish maps
 *    publicly. And `loginHint` does appear in the production bundle - that is
 *    `@complan/contracts`' `loginQuerySchema`, the API's own query surface, not
 *    this component.
 * 2. **The accounts do not exist anywhere else.** `planner`, `viewer` and the
 *    rest are subjects of `packages/api/dev/local-issuer.ts`. Against a real
 *    identity provider they resolve to nobody.
 * 3. **The one-click part is the issuer's, not ours.** Switching without a
 *    password works because the dev issuer auto-completes a `login_hint`. Entra
 *    ID treats `login_hint` as a pre-filled username and still authenticates the
 *    person, so the same link there is an ordinary sign-in prompt.
 *
 * `prompt=login` is what makes a *switch* possible at all: the issuer already
 * holds a session for the current account, and without it the authorization
 * request would be answered silently as that same account.
 */
import { useTranslation } from 'react-i18next';

/**
 * The dev issuer's accounts. Source of truth is
 * `packages/api/dev/local-issuer.ts`'s `DEV_ACCOUNTS`; kept in step by hand, the
 * same arrangement `packages/api/dev/local-database.ts` has with that file's group
 * ids. A name that has drifted shows up immediately as a signed-in user with no
 * roles.
 *
 * `label` is deliberately plain English rather than an i18n key: this control is
 * not part of the product surface, and putting throwaway dev strings into the
 * externalized catalogue (R18.9) would mean shipping them in every locale.
 */
const DEV_ACCOUNTS: readonly { readonly subject: string; readonly label: string }[] = [
  { subject: 'planner', label: 'Commercial Planning' },
  { subject: 'platform-owner', label: 'Platform Owner' },
  { subject: 'channel-owner', label: 'Channel Owner' },
  { subject: 'field-force', label: 'Field Force Leadership' },
  { subject: 'stakeholder', label: 'DTE/CSC Stakeholder' },
  { subject: 'viewer', label: 'Management Viewer' },
  { subject: 'admin', label: 'System Administrator' },
  { subject: 'no-roles', label: 'No roles (R1.3)' },
];

/**
 * Starts a sign-in as `subject`, returning to the current screen afterwards.
 *
 * A full page navigation rather than a `fetch`, because the exchange is a chain of
 * cross-origin redirects through the identity provider - the same reason
 * `beginLogin()` in `error-content.tsx` navigates. `target` carries the current
 * path so the retained-target machinery (R1.1) brings the user back where they
 * were, now as somebody else.
 */
function switchTo(subject: string): void {
  const target = `${window.location.pathname}${window.location.search}`;
  const query = new URLSearchParams({
    target,
    // Forces re-authentication: the issuer's existing session would otherwise
    // answer as the account that is already signed in.
    prompt: 'login',
    loginHint: subject,
  });
  window.location.assign(`/auth/login?${query.toString()}`);
}

/**
 * The switcher, or `null` in any production build.
 *
 * Returning `null` rather than having the caller conditionalise keeps the guard in
 * one place, next to the accounts it is guarding.
 */
export function DevAccountSwitcher({
  currentSubject,
}: {
  readonly currentSubject: string | undefined;
}): React.JSX.Element | null {
  const { t } = useTranslation();

  if (!import.meta.env.DEV) {
    return null;
  }

  // `value` is only a valid option when the signed-in subject is one of the dev
  // accounts; anything else (a real account, or none yet) shows the placeholder
  // rather than silently mislabelling who is signed in.
  const known = DEV_ACCOUNTS.some((account) => account.subject === currentSubject);

  return (
    <label className="flex items-center gap-1.5" title="Development only: sign in as another dev account">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-label">
        Dev user
      </span>
      <select
        aria-label="Development only: sign in as another dev account"
        className="border-line h-7 rounded border bg-white px-1.5 text-[11.5px] text-txt"
        value={known ? currentSubject : ''}
        onChange={(event) => {
          const next = event.target.value;
          if (next !== '' && next !== currentSubject) {
            switchTo(next);
          }
        }}
      >
        {known ? null : (
          <option value="">{currentSubject ?? t('common.account')}</option>
        )}
        {DEV_ACCOUNTS.map((account) => (
          <option key={account.subject} value={account.subject}>
            {account.subject} — {account.label}
          </option>
        ))}
      </select>
    </label>
  );
}
